import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Pardon Arc — late-game heat washout.
 *
 * Triggered when (FBI heat + SEC heat) >= 100 + cash >= $5M. 4
 * lobbying decisions decide whether you get the pardon (all heat -> 0,
 * permanent rep with maga +20 / left -25) or you don't (cost is real,
 * heat unchanged, and the public knows you tried).
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => number }[]; // returns "lobbying score" delta
}

const BEATS: Beat[] = [
  {
    text: 'YOUR LAWYER: "first move on the pardon. who do we route through?"',
    options: [
      { label: 'a senator we already own', effect: () => {
        useMarkets.getState().addCash(-500_000);
        usePower.getState().bumpInfluence(8);
        return 2;
      } },
      { label: 'a son-in-law', effect: () => {
        useMarkets.getState().addCash(-1_500_000);
        usePower.getState().bumpRep('press', -8);
        return 3;
      } },
      { label: 'just call him directly. you have the number.', effect: () => {
        usePower.getState().bumpRep('press', -4);
        return 1;
      } },
    ],
  },
  {
    text: 'PRESIDENTIAL ASSISTANT: "the President will see you. the price is a public endorsement."',
    options: [
      { label: 'pay it. endorse him. all of it.', effect: () => {
        useMarkets.getState().addCash(-2_000_000);
        usePower.getState().bumpRep('maga', 20);
        usePower.getState().bumpRep('left', -25);
        return 3;
      } },
      { label: 'pay just the \$2M. no endorsement.', effect: () => {
        useMarkets.getState().addCash(-2_000_000);
        usePower.getState().bumpRep('press', 4);
        return 1;
      } },
      { label: 'walk out. you\'re too principled.', effect: () => {
        usePower.getState().bumpRep('press', 8);
        return -2;
      } },
    ],
  },
  {
    text: 'THE PRESIDENT: "what do i get out of this?" you have one shot at this answer.',
    options: [
      { label: '"a friend who never forgets a favor."', effect: () => {
        usePower.getState().bumpInfluence(15);
        return 3;
      } },
      { label: '"i\'ll be at your next rally."', effect: () => {
        usePower.getState().bumpRep('maga', 12);
        return 2;
      } },
      { label: '"i\'ll donate \$10M to your library."', effect: () => {
        useMarkets.getState().addCash(-10_000_000);
        return 3;
      } },
    ],
  },
  {
    text: 'WAITING FOR THE PRESS RELEASE. last move?',
    options: [
      { label: 'have your PR flack pre-write the announcement', effect: () => {
        if (usePhone.getState().contacts['pr-flack']) return 2;
        return 0;
      } },
      { label: 'tweet a vague "thank you" 6 minutes before he announces', effect: () => {
        usePower.getState().bumpRep('press', -6);
        return 1;
      } },
      { label: 'sit quietly. let him drive the narrative.', effect: () => {
        return 1;
      } },
    ],
  },
];

let lobbyingScore = 0;

export function startPardonArc(): void {
  lobbyingScore = 0;
  useMeta.getState().recordLore('arc-pardon', 'Presidential Pardon');
  useCinematic.getState().fire({
    id: `pardon-${Date.now()}`,
    type: 'breaking',
    headline: 'PARDON SEASON.',
    subhead: 'your lawyer says you can buy a pardon. it will cost. it will be public. it will work. 4 decisions.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `pardon-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `pardon-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          lobbyingScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'lawyer',
        fromNpc: 'lawyer',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `pardon: move ${i + 1}/4` });
      sounds.notifyAlert();
    }, 6_000 + i * 30_000);
  }

  setTimeout(() => {
    if (lobbyingScore >= 7) {
      useCinematic.getState().fire({
        id: `pardon-granted-${Date.now()}`,
        type: 'ascension',
        headline: 'PARDONED.',
        subhead: 'all federal charges, dismissed. SEC heat: zero. FBI heat: zero. CNN spent 14 hours covering it. they spelled your name wrong every time.',
        durationMs: 7000,
      });
      useLife.getState().bumpFbiHeat(-100);
      useLife.getState().bumpSecHeat(-100);
      useLife.getState().bumpIrsHeat(-30);
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: a "well-known retail trader" has been granted a presidential pardon. legal experts call it "unusually broad." court-of-public-opinion verdict: also unusually broad.',
        likes: 56000, reposts: 18400,
      });
    } else {
      useCinematic.getState().fire({
        id: `pardon-denied-${Date.now()}`,
        type: 'breaking',
        headline: 'NO PARDON.',
        subhead: 'the President went golfing instead. your lobbying spend: gone. your heat: same. your reputation: now publicly known to have tried.',
        durationMs: 6500,
      });
      usePower.getState().bumpRep('press', -10);
      useLife.getState().bumpSanity(-12);
    }
  }, 6_000 + BEATS.length * 30_000 + 5_000);
}
